import AdminState from '../AdminState';
import React, { useState, useEffect, useRef } from "react";
import "./SliderMenu.css";
import { Button, Input } from '@chakra-ui/react';

// This represents a list of different options to choose from, such as labs or machines
// permissionFunction and checkPermissionFunction are optional parameters used to gray out unpermitted options
export default function SliderMenu({ placeholderText, dataFunction, selected, toggle, onClick, permissionFunction, checkPermissionFunction, }) {
   const [filterText, setFilterText] = useState("");
   const [data, setData] = useState([]);
   const [permissions, setPermissions] = useState(null);
   const [serverCallFailed, setServerCallFailed] = useState(false);
   const [loading, setLoading] = useState(true);
   const [retry, setRetry] = useState(0);

   // Selectable and unselectable (grayed out) data items
   const [selectableData, setSelectableData] = useState([]);
   const [unselectableData, setUnselectableData] = useState([]);

   // Refs for arrow key navigation
   const menuContainerRef = useRef(null);

   // User textbox that updates machine list accordingly
   const changeFilter = e => { setFilterText(e.target.value.toLowerCase()) };

   // Handle arrow key navigation at container level
   const handleContainerKeyDown = (e) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
         const container = menuContainerRef.current;
         if (!container) return;

         // Find all focusable buttons (not disabled, not tabIndex="-1")
         const allButtons = Array.from(container.querySelectorAll('button'));
         const focusableButtons = allButtons.filter(btn =>
            !btn.disabled && btn.tabIndex !== -1
         );
         if (focusableButtons.length === 0) return;

         const currentIndex = focusableButtons.findIndex(btn => btn === document.activeElement);

         if (currentIndex === -1) {
            // No button is focused, focus the first one
            if (focusableButtons.length > 0) {
               focusableButtons[0].focus();
            }
            return;
         }

         let nextIndex;
         if (e.key === 'ArrowUp') {
            nextIndex = currentIndex > 0 ? currentIndex - 1 : focusableButtons.length - 1;
         } else {
            nextIndex = currentIndex < focusableButtons.length - 1 ? currentIndex + 1 : 0;
         }

         e.preventDefault();
         focusableButtons[nextIndex].focus();
      }
   };

   useEffect(() => {
      let active = true;
      setLoading(true);
      setServerCallFailed(false);
      async function load() {
         try {
            const responses = await Promise.all([
               dataFunction(),
               permissionFunction ? permissionFunction() : Promise.resolve({ status: 200, items: null }),
            ]);
            if (responses.some(response => response.status !== 200)) throw new Error('load_failed');
            if (active) {
               setData(responses[0].items || []);
               setPermissions(responses[1].items);
            }
         } catch {
            if (active) setServerCallFailed(true);
         } finally {
            if (active) setLoading(false);
         }
      }
      load();
      return () => { active = false; };
   }, [dataFunction, toggle, permissionFunction, retry]);

   // Set selectable and unselectable data
   useEffect(() => {
      var selectable = [];
      var unselectable = [];
      if (checkPermissionFunction === undefined) {
         selectable = data;
      } else {
         data.map((e) => {
            if (checkPermissionFunction(permissions, e)) {
               selectable.push(e);
            } else {
               unselectable.push(e);
            }
         });
      }

      setSelectableData(selectable);
      setUnselectableData(unselectable);
   }, [data, permissions, checkPermissionFunction]);

   if (loading) {
      return <AdminState status="loading" title="Loading spaces…" />;
   }
   if (serverCallFailed) {
      return <AdminState status="error" title="Spaces could not be loaded"
         description="Pinpoint could not load the available spaces and permissions. Try again."
         onRetry={() => setRetry(value => value + 1)} />;
   } else {
      // Return a UI for the menu
      return (
         <div className="SliderMenu">
            <div className="SliderInput">
               <Input
                  aria-label={placeholderText}
                  value={filterText}
                  variant="outline"
                  size="md"
                  placeholder={placeholderText}
                  onChange={changeFilter}
               />
            </div>
            {data.length === 0 ? (
               <AdminState title="No spaces available" description="Spaces you can manage will appear here once you have been given access." />
            ) : !data.some(item => item.name.toLowerCase().includes(filterText)) ? (
               <AdminState title="No matching spaces" description="Try a different name or clear the search."
                  action={<Button variant="outline" size="sm" onClick={() => setFilterText('')}>Clear search</Button>} />
            ) : null}
            <div
               className="SliderContent"
               ref={menuContainerRef}
               onKeyDown={handleContainerKeyDown}
            >
               <ul>{
                  // Items the user has permissions for
                  selectableData.filter(datum =>
                     datum.name.toLowerCase().includes(filterText)
                  ).map((e) => {
                     return (
                        <SliderMenuEntry
                           key={e.name}
                           selected={selected === e ? true : false}
                           onClick={() => onClick(e)}
                           name={e.name}
                           selectable={true}
                        />);
                  })}
                  {// Items the user doesn't have permissions for
                     unselectableData.filter(datum =>
                        datum.name.toLowerCase().includes(filterText)
                     ).map((e) => {
                        return (
                           <SliderMenuEntry
                              key={e.name}
                              selected={false}
                              onClick={null}
                              name={e.name}
                              selectable={false}
                           />);
                     })}
               </ul>
            </div>
         </div>
      );
   }
}

function SliderMenuEntry(props) {
   const handleKeyDown = (e) => {
      if (props.onClick && (e.key === 'Enter' || e.key === ' ')) {
         e.preventDefault();
         props.onClick();
      }
      // Arrow keys are handled at container level
   };

   const buttonClass = `SliderMenuEntry ${props.selected ? 'selected' : 'unselected'} ${props.selectable ? 'selectable' : 'unselectable'}`;

   return (
      <li style={props.style}>
         {props.selectable ? (
            <button
               onClick={props.onClick}
               onKeyDown={handleKeyDown}
               className={buttonClass}
               aria-label={`Select ${props.name}`}
               aria-pressed={props.selected}
               type="button"
            >
               {props.name}
            </button>
         ) : (
            <button type="button" disabled
               className={buttonClass}
               aria-label={`${props.name} (not available)`}


            >
               {props.name}
            </button>
         )}
      </li>
   );
}
